<template>

  <v-container fluid class="mb-6 page-content order-list-placed">

    <!----------------------------->
    <!-- Page Label and Controls -->
    <!----------------------------->
    <v-container fluid class="pa-0">
      <v-row>
        <v-col cols="12" xs="12" sm="6" md="6" lg="6">
          <span class="mr-3 page-label">Ready to Schedule</span>
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="info-timezone" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
            </template>
            <div>Dates & times are: {{ activeTimezone }}</div>
          </v-tooltip>
        </v-col>
        <v-col cols="12" xs="12" sm="6" md="6" lg="6" class="d-flex align-start justify-center justify-sm-end">
          <!-- (selected data actions) -->
          <div v-if="orderItems.length > 0">
            <v-btn color="blue" width="180" :disabled="!buildRouteEnabled || selectedHasMixedTypes" @click="openDeliveryDateSelector">
              Build Route
            </v-btn>
            <div class="mt-2" style="text-align:center;" v-if="selectedItemCount > 0">{{ selectedItemCount }} Selected</div>
          </div>
        </v-col>
      </v-row>

      <!-- Selected item messages (when issues exist) -->
      <v-row class="py-2" v-if="selectedHasMixedTypes || selectedHasUnapprovedReceivers">
        <v-col cols="12" class="d-flex align-center justify-center selected-message-area">
          <v-icon color="amber darken-2" class="pr-2">mdi-alert-circle-outline</v-icon>
          <div v-if="selectedHasMixedTypes">You cannot create a route with both test and live orders.</div>
          <div v-else>Selected orders have unapproved receiver locations.</div>
        </v-col>
      </v-row>

      <v-row>
        <!-- Find by order number -->
        <v-col cols="12" xs="12" sm="12" md="7" lg="5" class="d-flex align-center justify-center justify-sm-start">
          <v-text-field
              clearable
              single-line
              hide-details
              style="max-width:300px;"
              class="ml-2 mb-2"
              label="Order number"
              append-icon="mdi-magnify"
              v-model="search" />
        </v-col>
        <!-- Filter by Live and Test -->
        <v-col cols="12" xs="12" sm="12" md="5" lg="4" class="">
          <div class="page-super-title">Show</div>
          <div class="d-flex align-center">
            <v-checkbox
                class="mt-0"
                label="Live Orders"
                hide-details
                @change="queryData"
                v-model="showLiveOrders" />
            <v-checkbox
                class="pl-8 mt-0"
                label="Test Orders"
                hide-details
                @change="queryData"
                v-model="showTestOrders" />
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!---------------->
    <!-- Order List -->
    <!---------------->
    <div class="mt-8">
      <v-data-table
          class="list-orders"
          :headers="headers"
          :items="orderItems"
          :search="search"
          :options.sync="dataOptions"
          :footer-props="paginationOptions"
          :loading="isDataLoading"
          :server-items-length="totalItems"
          :show-select="true"
          v-model="selected">

        <!-- header: notifications -->
        <!-- eslint-disable-next-line vue/no-unused-vars -->
        <template v-slot:[`header.notifications`]="{ header }">
          <v-icon small color="#c9c9c9">mdi-bell</v-icon>
        </template>

        <!-- notifications -->
        <template v-slot:[`item.notifications`]="{ item }">
          <v-tooltip top v-if="hasNotifications(item)">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-badge
                    bordered
                    style="cursor:pointer;"
                    color="error"
                    offset-x="12"
                    offset-y="12"
                    :content="getNotificationCount(item)">
                  <router-link :to="generateOrderMessagesPageLink(item)">
                    <v-btn icon>
                      <v-icon style="font-size:18px;">mdi-message-text-outline</v-icon>
                    </v-btn>
                  </router-link>
                </v-badge>
              </span>
            </template>
            <div>You have messages from the receiver</div>
          </v-tooltip>
        </template>

        <!-- routeleaf order number (link to order info page) -->
        <template v-slot:[`item.order_number`]="{ item }">
          <!-- Test Order indicator -->
          <div class="mb-2" v-if="item.is_test">
            <v-chip label color="orange darken-4" style="font-weight:500;">TEST ORDER</v-chip>
          </div>
          <!-- (auto-placed order due to previous delivery rejection or rescheduling) -->
          <template v-if="item.root_order_id">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="orange accent-3"
                    style="cursor:pointer;font-size:24px;margin-right:3px;padding-bottom:5px;"
                    v-bind="attrs"
                    v-on="on">
                  mdi-repeat-variant
                </v-icon>
              </template>
              <div style="font-weight:600">(Re-submitted order)</div>
              <!-- due to rejection -->
              <template v-if="item.parent_order && item.parent_order.rejected_at">
                <div>Last delivery was rejected by receiving agent:</div>
                <div v-if="item.parent_order.receiver_notes" class="mt-1">{{ item.parent_order.receiver_notes }}</div>
              </template>
              <!-- due to rescheduling -->
              <template v-if="item.parent_order && item.parent_order.rescheduled_at">
                <div>Last delivery was rescheduled:</div>
                <div v-if="item.parent_order.dispatcher_notes && item.parent_order.dispatcher_notes.reschedule" class="mt-1">
                  {{ item.parent_order.dispatcher_notes.reschedule }}
                </div>
              </template>
            </v-tooltip>
          </template>
          <span style="font-size:1.2em">
            <router-link :to="generateOrderInfoPageLink(item)">
              {{ item.thirdparty_order_number }}
            </router-link>
          </span>
        </template>

        <!-- order total -->
        <template v-slot:item.order_details.total="{ item }">
          <span class="order-total">{{ formatTotal(item.order_details) }}</span>
        </template>

        <!-- provider alias -->
        <template v-slot:item.provider_alias="{ item }">
          <span class="provider-name">{{ formatProvider(item.provider_alias) }}</span>
        </template>

        <!-- provider account -->
        <template v-slot:[`item.thirdparty_account_name`]="{ item }">
          <span>{{ item.thirdparty_account_name }}</span>
        </template>

        <!-- provider brand -->
        <template v-slot:[`item.thirdparty_brand_name`]="{ item }">
          <span>{{ item.thirdparty_brand_name }}</span>
        </template>

        <!-- pickup location (shipper) -->
        <template v-slot:[`item.shipper.name`]="{ item }">
          <router-link :to="generateLocationPageLink(item.shipper)">
            {{ formatLocation(item.shipper) }}
          </router-link>
        </template>

        <!-- dropoff location (receiver) -->
        <template v-slot:[`item.receiver.name`]="{ item }">
          <!-- (note on approval requirements) -->
          <v-tooltip top v-if="item.receiver.receiver_status !== 'APPROVED'">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="red darken-2"
                  style="cursor:pointer;font-size:21px;margin-right:3px;"
                  v-bind="attrs"
                  v-on="on">
                mdi-alert
              </v-icon>
            </template>
            <div>Order cannot be scheduled until receiver location is approved.</div>
          </v-tooltip>

          <router-link :to="generateLocationPageLink(item.receiver)">
            {{ formatLocation(item.receiver) }}
          </router-link>

          <!-- (note on requires appointment) -->
          <v-tooltip top v-if="item.receiver.requires_appointment">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="amber darken-1"
                  style="cursor:pointer;font-size:19px;margin-left:5px;"
                  v-bind="attrs"
                  v-on="on">
                mdi-calendar-clock-outline
              </v-icon>
            </template>
            <!-- <div v-if="item.receiver.notes">{{ item.receiver.notes }}</div> -->
            <div>Appointment is required before delivery.</div>
          </v-tooltip>
        </template>

        <!-- expected delivery date -->
        <template v-slot:item.expected_delivery="{ item }">
          <span>{{ formatDate(item.expected_delivery) }}</span>
        </template>

        <!-- placed date -->
        <template v-slot:item.placed_at="{ item }">
          <span>{{ formatDateTime(item.placed_at) }}</span>
        </template>

        <!-- order status -->
        <template v-slot:item.order_status="{ item }">
          <v-chip label outlined :class="item.order_status">{{ item.order_status }}</v-chip>
        </template>
      </v-data-table>
    </div>

    <!-- Delivery Date Selector and Route Creation Feedback -->
    <v-dialog persistent width="420" v-model="isDeliveryDateSelectorOpen">
      <v-card align="center">
        <!-- (Route creating message) -->
        <template v-if="isRouteBuilding">
          <v-card-text>
            <v-container class="d-flex flex-column align-center justify-center">
              <v-progress-circular indeterminate :size="45" style="margin:30px 0" color="primary" />
            </v-container>
            <div class="text-h6">Creating Route...</div>
          </v-card-text>
        </template>
        <!-- (Route creation error) -->
        <template v-else-if="routeBuildingError">
          <v-card-text style="margin:35px 0 10px 0">
            <v-icon large color="error" class="pb-6">mdi-alert-circle-outline</v-icon>
            <div class="route-building-error-text">{{ routeBuildingError }}</div>
            <v-btn large class="mt-8" @click="cancelDeliveryDateSelection">
              Close
            </v-btn>
          </v-card-text>
        </template>
        <!-- Delivery date selector -->
        <template v-else>
          <v-card-title class="text-h5">
            Select Delivery Date
          </v-card-title>
          <v-card-text class="date-picker-body">
            <v-date-picker
                v-if="isDeliveryDateSelectorOpen"
                :show-current="earliestDeliveryDateForSelected"
                v-model="deliveryDate" />
          </v-card-text>
          <v-card-actions class="mt-4 mx-2">
            <v-btn text @click="cancelDeliveryDateSelection">
              Cancel
            </v-btn>
            <v-spacer />
            <v-btn color="primary" width="100" @click="buildRoute">
              Select
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

  </v-container> <!-- END page-content -->

</template>

<script>
/* global moment */
import dateTimeUtils from '@/utils/date-time-utils'

export default {
  name: 'OrderListPlacedPage',

  data () {
    return {
      headers: [
        { text: 'Notifications', value: 'notifications', sortable: false, width: '48px' },
        { text: 'Order #', value: 'order_number' },
        // { text: 'Provider', value: 'provider_alias' },
        // { text: 'Account', value: 'thirdparty_account_name' },
        { text: 'Brand', width: '140px', value: 'thirdparty_brand_name' },
        { text: 'Total', value: 'order_details.total' },
        { text: 'From', value: 'shipper.name' },
        { text: 'To', value: 'receiver.name' },
        { text: 'Expected Delivery', value: 'expected_delivery', align: 'center' },
        { text: 'Placed', value: 'placed_at', align: 'center' },
        { text: 'Status', value: 'order_status' }
      ],
      isDataLoading: false,
      dataLoadingError: null,
      isRouteBuilding: false,
      routeBuildingError: null,
      orderData: null,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50, 100]
      },
      dataOptions: {
        itemsPerPage: 10,
        // NOTE - Currently enabled for single field sort only !!!
        sortBy: ['placed_at'],
        sortDesc: [true]
      },
      search: null,
      showTestOrders: true,
      showLiveOrders: true,
      selected: [],
      isDeliveryDateSelectorOpen: false,
      deliveryDate: null
    }
  },

  computed: {
    activeTimezone () {
      return this.$store.getters['app/defaultTimezone']
    },
    orderItems () {
      return (this.orderData) ? this.orderData.items : []
    },
    dataMeta () {
      return (this.orderData) ? this.orderData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    },
    selectedItemCount () {
      return (this.selected) ? this.selected.length : 0
    },
    // Returns true if a mix of test order and live orders has been selected
    selectedHasMixedTypes () {
      return (
        this.selectedItemCount > 0 &&
        this.selected.some(item => item.is_test === true) &&
        this.selected.some(item => item.is_test === false)
      )
    },
    // Returns true if orders with unapproved receivers has been selected
    selectedHasUnapprovedReceivers () {
      return (
        this.selectedItemCount > 0 &&
        this.selected.some(item => item.receiver.receiver_status !== 'APPROVED')
      )
    },
    // Determines if proper selection has been made (to build a route)
    buildRouteEnabled () {
      // Only permit routes to be created for PLACED orders that have APPROVED receivers
      return (
        this.selectedItemCount > 0 &&
        !this.selected.find(item => {
          return item.order_status !== 'PLACED' || item.receiver.receiver_status !== 'APPROVED'
        })
      )
    },
    // Returns the earliest expected delivery date from the selected orders (or current date if none selected)
    earliestDeliveryDateForSelected () {
      if (this.selectedItemCount > 0) {
        const moments = this.selected.filter(item => item.expected_delivery).map(item => moment(item.expected_delivery))
        return this.$root.utils.format(moment.min(moments), 'YYYY-MM-DD')
      } else {
        return this.$root.utils.format(new Date(), 'YYYY-MM-DD')
      }
    },
    notifications () {
      return this.$parent.notifications
    }
  },

  watch: {
    // Observe changes invoked by the data-table component (e.g. pagination, sorting)
    dataOptions: {
      handler () {
        this.queryData()
      },
      deep: true
    },
    // Observe changes to the search field
    search: function (updated) {
      this.queryData()
    }
  },

  methods: {
    // Display a date
    formatDate (date) {
      return (date) ? dateTimeUtils.format(date, 'MMMM D, YYYY') : '-'
    },

    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.format(date, 'MMM D, YYYY h:mma') : ''
    },

    // Display total
    formatTotal (orderDetails = {}) {
      const totalAsNum = (orderDetails.total) ? Number(orderDetails.total) : 0
      return `$${totalAsNum.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    },

    // Display provider name
    formatProvider (alias) {
      return (alias) ? this.$root.providerAliasToName(alias) : ''
    },

    // Display location name
    formatLocation (loc) {
      return (loc && loc.name) ? loc.name : '-'
    },

    // Get notifications for the provided order
    getNotificationCount (order) {
      if (!order || !order.id) return 0
      return (this.notifications && this.notifications.length > 0)
        ? this.notifications.filter(it => it.order_id === order.id).length
        : 0
    },

    // Checks if an order has notifications
    hasNotifications (order) {
      return this.getNotificationCount(order) > 0
    },

    // Generate link data for shipper/receiver page (for a provided location item)
    generateLocationPageLink (loc) {
      if (loc && loc.model) {
        if (loc.model === 'Shipper') {
          return {
            name: 'shipper-item',
            params: { shipperId: loc.id }
          }
        } else if (loc.model === 'Receiver') {
          return {
            name: 'receiver-item',
            params: { receiverId: loc.id }
          }
        } else {
          return ''
        }
      } else {
        return ''
      }
    },

    // Generate link data for order info page
    generateOrderInfoPageLink (item) {
      if (item && item.model) {
        return {
          name: 'order-info-overview',
          params: { orderId: item.id }
        }
      } else {
        return ''
      }
    },

    // Generate link data for order messages page
    generateOrderMessagesPageLink (item) {
      if (item && item.model) {
        return {
          name: 'order-info-messages',
          params: { orderId: item.id }
        }
      } else {
        return ''
      }
    },

    // Cancels date selection by closing selector and clearing any selections
    cancelDeliveryDateSelection () {
      this.isDeliveryDateSelectorOpen = false
      this.deliveryDate = null
      this.routeBuildingError = null
    },

    // Opens the delivery date selector
    openDeliveryDateSelector () {
      this.isDeliveryDateSelectorOpen = true
    },

    // Perform query of placed orders
    async queryData () {
      this.isDataLoading = true

      try {
        // Build sort
        // NOTE - Currently enabled for single field sort only !!!
        let orderBy = null
        const sortBy = this.dataOptions.sortBy
        const sortDesc = this.dataOptions.sortDesc
        if (sortBy && sortBy.length > 0) {
          const isDesc = (sortDesc && sortDesc.length > 0) ? sortDesc[0] : false
          const prefix = (isDesc) ? '-' : ''
          orderBy = `${prefix}${sortBy[0]}`
        }

        // Build pagination
        const page = this.dataOptions.page || 1
        const limit = this.dataOptions.itemsPerPage
        const skip = (page === 1) ? 0 : (page - 1) * limit

        // Build request input
        const params = {
          skip,
          limit,
          status: ['PLACED'],
          with: 'shipper,receiver,order_details,parent_order'
        }

        // Apply test/live filters
        if (this.showTestOrders && !this.showLiveOrders) params.test_only = true
        if (this.showLiveOrders && !this.showTestOrders) params.live_only = true

        // Apply sorting
        if (orderBy) params.order_by = orderBy

        // Apply search query (for order_number only)
        if (this.search) params['order_number.contains'] = this.search

        // console.log('[DEVING] query data:', this.dataOptions, params)

        // Retrieve results
        this.orderData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'orders',
          opts: { params }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    },

    // Create a new route with the selected orders
    async buildRoute () {
      this.isRouteBuilding = true

      const deliveryDate = dateTimeUtils.format(this.deliveryDate)
      const deliveryDateForName = dateTimeUtils.format(this.deliveryDate, 'YYYY-MM-DD')
      const orderIds = this.selected.map(order => order.id)
      const routeData = {
        name: `Route ${deliveryDateForName}`,
        delivery_date: deliveryDate,
        order_ids: orderIds,
        route_info: { manual: true }
      }
      try {
        // Create route
        const route = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: 'route',
          opts: {
            bodyData: routeData
          }
        })

        // Go to route builder page
        this.$root.goTo('route-builder', { routeId: route.id })
      } catch (err) {
        this.routeBuildingError = err.message || err.text || err
      } finally {
        this.isRouteBuilding = false
      }
    }
  }
}
</script>

<style>
  .order-list-placed .info-timezone {
    font-size: 1.25em;
    color: #6a6a6a;
    padding-bottom: 12px;
    cursor:pointer;
  }

  .order-list-placed .selected-message-area {
    font-size: 1.1rem;
    color: #c9c9c9;
    border: 2px solid #FF9D29;
    border-radius: 4px;
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
  }

  .route-building-error-text {
    font-size: 1.07rem;
  }

  .v-date-picker-title__date {
    display: none;
  }

  .v-date-picker-title__year {
    font-size: 18px;
  }

  .date-picker-body {
    padding-bottom: 4px !important;
  }

  .order-list-placed .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .order-list-placed .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .order-list-placed .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 110px;
  }

  .order-list-placed .v-data-table a {
    text-decoration: none;
  }
  .order-list-placed .v-data-table a:hover {
    text-decoration: underline;
  }
</style>
